import { isApiError } from '../../types/common';
import type { ResultadoDaChamada } from './tipos';

/**
 * Fontes e exames respondem o lote item a item (#606, D4): `success` com os ids gravados e
 * `failed` com o motivo de cada um — a duplicidade vem com o código `DUPLICATE_APPLICATION`, para
 * virar "já existia" e não falha. Com um id só, o servidor vai pelo caminho unitário: responde
 * sem lista quando grava e recusa com 409 e o mesmo código quando já existia.
 */
const DUPLICADA = 'DUPLICATE_APPLICATION';

interface FalhaDoLote {
  error?: string;
  code?: string;
  [campo: string]: unknown;
}

interface LoteDoServidor {
  success: unknown[];
  failed: FalhaDoLote[];
}

function eLote(valor: unknown): valor is LoteDoServidor {
  return (
    typeof valor === 'object' &&
    valor !== null &&
    Array.isArray((valor as LoteDoServidor).success) &&
    Array.isArray((valor as LoteDoServidor).failed)
  );
}

function loteDaResposta(corpo: unknown): LoteDoServidor | null {
  if (eLote(corpo)) return corpo;
  if (typeof corpo === 'object' && corpo !== null && 'data' in corpo && eLote(corpo.data)) return corpo.data;
  return null;
}

/**
 * Faz a chamada e separa o que foi aplicado, o que já existia e o que falhou. `campoDoId` é o
 * nome do id do item em cada falha (`sourceGeneratorId`, `examId`). Erro que não é duplicidade
 * de um id só segue adiante: o assistente marca a chamada inteira como falha.
 */
export async function aplicarComResultadoPorItem(
  ids: number[],
  campoDoId: string,
  chamar: () => Promise<unknown>
): Promise<ResultadoDaChamada> {
  let corpo: unknown;
  try {
    corpo = await chamar();
  } catch (erro: unknown) {
    if (ids.length === 1 && isApiError(erro) && erro.code === DUPLICADA) {
      return { aplicados: [], jaExistiam: ids, falharam: [] };
    }
    throw erro;
  }

  const lote = loteDaResposta(corpo);
  if (!lote) return { aplicados: ids, jaExistiam: [], falharam: [] };
  const idDa = (falha: FalhaDoLote) => Number(falha[campoDoId]);
  return {
    aplicados: lote.success.map(Number),
    jaExistiam: lote.failed.filter((falha) => falha.code === DUPLICADA).map(idDa),
    falharam: lote.failed
      .filter((falha) => falha.code !== DUPLICADA)
      .map((falha) => ({ itemId: idDa(falha), motivo: falha.error ?? 'O servidor não aplicou' })),
  };
}
